import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Shield, UserCircle, Mail, Phone, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const AdminUsersManagePage = () => {
  const { token, admin } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'cashier' });
  const [showPassword, setShowPassword] = useState(false);

  const ROLES = [
    { value: 'super_admin', label: 'Super Admin', color: isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700' },
    { value: 'manager', label: 'Manager', color: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700' },
    { value: 'supervisor', label: 'Supervisor', color: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700' },
    { value: 'cashier', label: 'Cashier', color: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700' },
    { value: 'content_manager', label: 'Content Manager', color: isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700' }
  ];

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/admins`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) setAdmins(await res.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const saveAdmin = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editingAdmin && !form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!editingAdmin && !form.password.trim()) {
      toast.error('Password is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingAdmin ? `${API}/admin/admins/${editingAdmin.admin_id}` : `${API}/admin/admins`;
      
      let body;
      if (editingAdmin) {
        // When editing, include password only if it's provided
        body = { 
          name: form.name, 
          phone: form.phone, 
          role: form.role, 
          is_active: editingAdmin.is_active 
        };
        if (form.password && form.password.trim()) {
          body.password = form.password;
        }
      } else {
        body = form;
      }
      
      const res = await fetch(url, {
        method: editingAdmin ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) { toast.success(editingAdmin ? 'Updated' : 'Created'); setShowModal(false); setEditingAdmin(null); setShowPassword(false); resetForm(); fetchAdmins(); setSaving(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); setSaving(false); }
    } catch (error) { toast.error('Error'); setSaving(false); }
  };

  const toggleAdminStatus = async (adminId, isActive) => {
    try {
      const res = await fetch(`${API}/admin/admins/${adminId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      toast.success(`Admin ${isActive ? 'deactivated' : 'activated'}`);
      fetchAdmins();
    } catch (error) { toast.error('Failed'); }
  };

  const deleteAdmin = async (adminId) => {
    if (adminId === admin.admin_id) { toast.error("Can't delete yourself"); return; }
    if (!confirm('Delete this admin?')) return;
    try {
      const res = await fetch(`${API}/admin/admins/${adminId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) { toast.success('Deleted'); fetchAdmins(); }
    } catch (error) { toast.error('Failed'); }
  };

  const resetForm = () => setForm({ name: '', email: '', password: '', phone: '', role: 'cashier' });
  const getRoleBadge = (role) => {
    const roleInfo = ROLES.find(r => r.value === role) || ROLES[3];
    return <span className={`px-2 py-1 rounded text-xs font-medium ${roleInfo.color}`}>{roleInfo.label}</span>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Admin Users</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage administrator accounts</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" /> Add Admin
          </Button>
        </div>

        <Card className={theme.card}>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12"><div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div></div>
            ) : admins.length === 0 ? (
              <p className={`text-center py-12 ${theme.textMuted}`}>No admin users found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm border-b ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                      <th className="p-4 font-medium">Admin</th>
                      <th className="p-4 font-medium">Contact</th>
                      <th className="p-4 font-medium">Role</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Created</th>
                      <th className="p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((a) => (
                      <tr key={a.admin_id} className={`border-b text-sm ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                              <UserCircle className={`h-6 w-6 ${theme.textMuted}`} />
                            </div>
                            <div>
                              <p className={`font-medium ${theme.text}`}>{a.name}</p>
                              <p className={`text-xs ${theme.textSubtle}`}>{a.admin_id.slice(-8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="space-y-1">
                            <p className={`flex items-center gap-1 ${theme.text}`}><Mail className="h-3 w-3" /> {a.email}</p>
                            {a.phone && <p className={`flex items-center gap-1 text-xs ${theme.textMuted}`}><Phone className="h-3 w-3" /> {a.phone}</p>}
                          </div>
                        </td>
                        <td className="p-4">{getRoleBadge(a.role)}</td>
                        <td className="p-4">
                          {a.is_active ? (
                            <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}><CheckCircle className="h-3 w-3" /> Active</span>
                          ) : (
                            <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}><XCircle className="h-3 w-3" /> Inactive</span>
                          )}
                        </td>
                        <td className={`p-4 ${theme.textMuted}`}>{new Date(a.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className={theme.textMuted} onClick={() => { setEditingAdmin(a); setForm({ name: a.name, email: a.email, password: '', phone: a.phone || '', role: a.role }); setShowModal(true); }}><Edit className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className={a.is_active ? 'text-amber-400' : 'text-emerald-400'} onClick={() => toggleAdminStatus(a.admin_id, a.is_active)}>{a.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}</Button>
                            {a.admin_id !== admin.admin_id && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => deleteAdmin(a.admin_id)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Permissions Info */}
        <Card className={theme.card}>
          <CardHeader><CardTitle className={theme.text}>Role Permissions</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {ROLES.map((role) => (
                <div key={role.value} className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className={`h-4 w-4 ${theme.textMuted}`} />
                    <span className={`px-2 py-1 rounded text-xs font-medium ${role.color}`}>{role.label}</span>
                  </div>
                  <ul className={`text-xs space-y-1 ${theme.textMuted}`}>
                    {role.value === 'super_admin' && <><li>• Full system access</li><li>• Manage all admins</li><li>• Manage memberships</li><li>• View all reports</li></>}
                    {role.value === 'manager' && <><li>• Manage users & courts</li><li>• Create events</li><li>• View reports</li><li>• Handle support</li></>}
                    {role.value === 'supervisor' && <><li>• Manage bookings</li><li>• Block courts</li><li>• POS access</li><li>• Open play management</li></>}
                    {role.value === 'cashier' && <><li>• POS access</li><li>• View bookings</li><li>• Dashboard view</li></>}
                    {role.value === 'content_manager' && <><li>• Embed Posts access</li><li>• Manage social content</li><li>• Manage content manager accounts</li><li>• Dashboard view</li></>}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Admin Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>{editingAdmin ? 'Edit Admin' : 'Add Admin'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label className={theme.text}>Name</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className={theme.input} placeholder="John Doe" /></div>
                <div><Label className={theme.text}>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className={theme.input} placeholder="admin@example.com" disabled={!!editingAdmin} /></div>
                <div>
                  <Label className={theme.text}>
                    Password {editingAdmin && <span className={`text-xs ${theme.textMuted}`}>(Leave blank to keep current)</span>}
                  </Label>
                  <div className="relative">
                    <Input 
                      type={showPassword ? "text" : "password"} 
                      value={form.password} 
                      onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} 
                      className={`${theme.input} pr-10`} 
                      placeholder={editingAdmin ? "Enter new password" : "••••••••"} 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.textMuted} hover:opacity-80`}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div><Label className={theme.text}>Phone (Optional)</Label><Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} className={theme.input} placeholder="+63 XXX XXX XXXX" /></div>
                <div>
                  <Label className={theme.text}>Role</Label>
                  <select value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    {ROLES.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => { setShowModal(false); setEditingAdmin(null); setShowPassword(false); }}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={saveAdmin} disabled={saving}>{editingAdmin ? 'Update' : 'Create'}</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsersManagePage;
